import { useToast } from '@/hooks/use-toast'
import http from '@/lib/http'
import type { AdditionalNotesType, Investigation, MedRequest } from '@/types/data'
import { useMutation } from '@tanstack/react-query'
import { queryClient } from '@/services/queryClient'
import { resetInvestigationStore } from '@/lib/stores/investigation'
import { isUserTrialPrescriber } from '@/lib/permissions'
import { useUtilityStore } from '@/lib/stores/utils'

export const removeInvestigationByIdFromCache = (id: string) =>
  queryClient.setQueryData(['investigations'], (old: (MedRequest | Investigation)[]) => {
    return old.filter(investigationObj => investigationObj.id !== id)
  })

const useInvestigationAction = () => {
  const { toast } = useToast()
  const isTrialPrescriber = isUserTrialPrescriber()

  return useMutation({
    mutationKey: ['investigationAction'],
    mutationFn: ({
      id,
      action,
      message,
      additionalNotes
    }: {
      id: string
      action: 'approve' | 'contact'
      message: string // Either approval reason or contact message
      additionalNotes?: AdditionalNotesType
    }) => {
      // Only perform actual mutation actions if the user is not a trial prescriber.
      if (isTrialPrescriber) {
        const { actionedInvestigationIds, setActionedInvestigationIds } = useUtilityStore.getState()
        setActionedInvestigationIds(Array.from(new Set([...actionedInvestigationIds, id])))

        queryClient.invalidateQueries({ queryKey: ['investigations'] })
        queryClient.invalidateQueries({
          queryKey: ['totalMedicationRequests']
        })

        return Promise.resolve(null)
      }

      return http.post('/prescriber/investigations/update', {
        id,
        action,
        additionalNotes,
        message
      })
    },
    onMutate({ id }) {
      removeInvestigationByIdFromCache(id)
      resetInvestigationStore()
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['investigationRequestById'] })
      queryClient.invalidateQueries({ queryKey: ['investigations'] })
    },
    onError() {
      toast({
        title: 'Error',
        description: 'Error taking action for investigation'
      })
    }
  })
}

export default useInvestigationAction
